// =============================================================================
// File: frontend/src/components/AIAssistant/TrendsAnalysis.jsx
// Analytics & Reporting Dashboard — KPI cards, trend charts, AI analysis, XAI
// Sprint 18 Phase A/E
// Patent claims exercised: P-003 Claims 4-8,15-17, P-001 Claims 4,7,8,11-14
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../../config';
import XaiPromptPanel from '../XaiPromptPanel';
import { CitationsButton } from '../Citations';
import { citationCount } from '../../utils/xaiHelpers';
import { handleAIError } from '../../utils/aiErrorHandler';
import MedicalDisclaimer from '../MedicalDisclaimer';
import CostChip from '../CostChip';
import HHSInstallNudge from '../HHSInstallNudge';
import { useCreditSpend } from '../../contexts/CreditSpendContext';

const DATE_RANGES = [
  { label: '7 Days', value: 7 },
  { label: '14 Days', value: 14 },
  { label: '30 Days', value: 30 },
  { label: '90 Days', value: 90 },
];

const CATEGORY_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'nutrition', label: 'Nutrition' },
  { id: 'activity', label: 'Activity' },
  { id: 'sleep', label: 'Sleep' },
  { id: 'body', label: 'Body' },
  { id: 'vitals', label: 'Vitals' },
];

const TREND_ICONS = {
  overview: '\u{1F4CA}',
  nutrition: '\u{1F957}',
  activity: '\u{1F3C3}',
  sleep: '\u{1F634}',
  hydration: '\u{1F4A7}',
  body: '\u{2696}\u{FE0F}',
  vitals: '\u{2764}\u{FE0F}',
};

const TrendsAnalysis = ({ user, token, fetchWithAuth, onClose, onBack, setActiveTab,
                         initialMainTab = 'trends' }) => {
  const { spend } = useCreditSpend();  // #77 analytics_ai = 1 credit (subtle chip, low-balance confirm)
  const [days, setDays] = useState(30);
  // `initialMainTab` lets a notification deep link (/analytics?tab=objectives — the #89
  // achievement prompt) open straight on Objectives. Defaults to 'trends' for every other
  // entry point, so normal navigation is unchanged.
  const [mainTab, setMainTab] = useState(initialMainTab);
  const [category, setCategory] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [trendData, setTrendData] = useState(null);
  const [error, setError] = useState(null);

  // AI Analysis state
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showXai, setShowXai] = useState(false);

  // Reports tab state
  const [reports, setReports] = useState([]);
  const [trainerReports, setTrainerReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [accessHistory, setAccessHistory] = useState([]);  // P-004 claim 18
  // Send Report inline state
  const [sendMode, setSendMode] = useState(null); // 'provider' | 'trainer' | null
  const [providers, setProviders] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [sendProgress, setSendProgress] = useState(0);
  const [sendingReport, setSendingReport] = useState(false);
  const [sendComplete, setSendComplete] = useState(false);
  const [viewingReportUrl, setViewingReportUrl] = useState(null);
  const [viewingReportTitle, setViewingReportTitle] = useState('');
  const [reportPdfLoading, setReportPdfLoading] = useState(false);

  const authFetch = useCallback(async (url, options = {}) => {
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    };
    if (fetchWithAuth) {
      return fetchWithAuth(url, { ...options, headers });
    }
    return fetch(url, { ...options, headers });
  }, [token, fetchWithAuth]);

  // Load trend data whenever days or category changes
  useEffect(() => {
    loadTrends();
  }, [days, category]);

  const loadTrends = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(
        `${API_BASE}/api/analytics/trends?days=${days}&category=${category}`
      );
      if (res.ok) {
        const data = await res.json();
        setTrendData(data);
      } else {
        setError('Failed to load analytics data');
      }
    } catch (err) {
      setError('Network error loading analytics');
    } finally {
      setLoading(false);
    }
  };

  const runAiAnalysis = async () => {
    setAiLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/analytics/ai-analyze?days=${days}`, {
        method: 'POST',
      });
      if (res.ok) {
        const data = await res.json();
        setAiAnalysis(data);
        // Store generation timestamp to enforce once-daily limit
        localStorage.setItem('healist_ai_analysis_date', new Date().toISOString().split('T')[0]);
        localStorage.setItem('healist_ai_analysis_days', String(days));
      } else if (await handleAIError(res)) {
        // unified AI limit modal shown
      }
    } catch (err) {
      console.error('AI analysis error:', err);
    } finally {
      setAiLoading(false);
    }
  };

  // Check if AI analysis was already generated today for this duration
  const isAiAnalysisGeneratedToday = () => {
    const savedDate = localStorage.getItem('healist_ai_analysis_date');
    const savedDays = localStorage.getItem('healist_ai_analysis_days');
    const today = new Date().toISOString().split('T')[0];
    return savedDate === today && savedDays === String(days) && aiAnalysis !== null;
  };

  // Clear cached AI analysis only when days changes (not category — all categories in one response)
  useEffect(() => {
    setAiAnalysis(null);
  }, [days]);

  // ── KPI Card ──
  // M-13 (Dev audit 2026-10-20): the server always sent `unit`, `change` and `change_pct`, but this card read
  // `change_text` / `change_direction`, which the server never sends, so units and the period-over-period
  // change never showed. The change is shown NEUTRALLY: whether "up" is good depends on the metric and the
  // user's own goal (weight down, resting HR down), and an AI-free tile must not pass that judgement.
  const renderKpiCard = (kpi) => {
    if (!kpi) return null;
    const hasChange = typeof kpi.change === 'number';
    const arrow = !hasChange ? '' : kpi.change > 0 ? '\u2191' : kpi.change < 0 ? '\u2193' : '\u2192';
    const signed = hasChange ? `${kpi.change > 0 ? '+' : ''}${kpi.change}` : '';
    const pct = typeof kpi.change_pct === 'number' ? ` (${kpi.change_pct > 0 ? '+' : ''}${kpi.change_pct}%)` : '';
    return (
      <div className="ta-kpiCard" key={kpi.label} data-testid="kpi-card">
        <div className="ta-kpiValue">
          {kpi.value ?? '--'}
          {kpi.value != null && kpi.unit && <span className="ta-kpiUnit" data-testid="kpi-unit"> {kpi.unit}</span>}
        </div>
        <div className="ta-kpiLabel">{kpi.label}</div>
        {hasChange && (
          <div className="ta-kpiChange" data-testid="kpi-change">
            {arrow} {signed}{kpi.unit && kpi.unit !== '/100' ? ` ${kpi.unit}` : ''}{pct} vs previous period
          </div>
        )}
      </div>
    );
  };

  // ── Bar Chart ──
  const renderBarChart = (chartData) => {
    if (!chartData || !chartData.points || chartData.points.length === 0) {
      return (
        <div className="ta-chartEmpty">
          <p>No chart data available for this period</p>
        </div>
      );
    }
    const values = chartData.points.map(p => p.value || 0);
    const maxVal = Math.max(...values, 1);
    return (
      <div className="ta-chartContainer">
        {chartData.title && <div className="ta-chartTitle">{chartData.title}</div>}
        <div className="ta-chartBars">
          {chartData.points.map((point, i) => (
            <div className="ta-chartBarCol" key={i}>
              <div
                className="ta-chartBar"
                style={{ height: `${Math.max((point.value / maxVal) * 100, 2)}%` }}
                title={`${point.label}: ${point.value}`}
              />
              <div className="ta-chartBarLabel">{point.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Trend Summary Card ──
  const renderTrendSummary = (summary) => {
    if (!summary) return null;
    const statusClass = summary.status === 'improving' ? 'ta-summaryStatus--improving'
      : summary.status === 'declining' ? 'ta-summaryStatus--declining'
      : 'ta-summaryStatus--stable';
    const statusLabel = summary.status === 'improving' ? 'Improving'
      : summary.status === 'declining' ? 'Needs Attention'
      : 'Stable';
    return (
      <div className="ta-summaryCard" key={summary.category}>
        <div className="ta-summaryHeader">
          <span>{TREND_ICONS[summary.category] || ''} {summary.title}</span>
          <span className={`ta-summaryStatus ${statusClass}`}>{statusLabel}</span>
        </div>
        <div className="ta-summaryBody">
          {summary.highlights && summary.highlights.map((h, i) => (
            <span key={i} className="ta-summaryHighlight">{h}</span>
          ))}
        </div>
      </div>
    );
  };

  // ── AI Analysis Panel ──
  const renderAiAnalysis = () => {
    if (!aiAnalysis && !aiLoading) {
      return (
        <div className="ta-aiSection">
          <button
            className="btn btn-primary ta-aiButton"
            onClick={() => spend('analytics_ai', 'Generate AI trend analysis?', runAiAnalysis)}
            disabled={aiLoading}
          >
            Generate AI Trend Analysis <CostChip apiType="analytics_ai" />
          </button>
          <p className="ta-aiHint">
            AI will analyze all 6 health domains in one call for the selected time range.
            {isAiAnalysisGeneratedToday() && ' (Already generated today for this range)'}
          </p>
        </div>
      );
    }

    if (aiLoading) {
      return (
        <div className="ta-aiSection">
          <div className="ta-loadingContainer">
            <div className="ta-spinner"></div>
            <p>Analyzing all health domains with AI...</p>
          </div>
        </div>
      );
    }

    // Response contains analysis per category — pick the active one
    const allAnalysis = aiAnalysis?.analysis;
    if (!allAnalysis) return null;
    const analysis = allAnalysis[category] || allAnalysis.overview || {};

    return (
      <div className="ta-aiResults">
        <div className="ta-aiResultsHeader">
          <h4>AI Trend Analysis</h4>
          <div className="ta-aiResultsActions">
            {/* Apple 1.4.1 (2026-10-17): the standard Citations control. This also fixes the
                old label, which rendered a literal "&amp;" (an HTML entity in a JS string). */}
            <CitationsButton
              open={showXai}
              count={citationCount(aiAnalysis?.prompt_data, 'Trend Analysis')}
              onToggle={() => setShowXai(!showXai)}
            />
          </div>
        </div>

        {showXai && aiAnalysis?.prompt_data && (
          <XaiPromptPanel
            promptData={aiAnalysis.prompt_data}
            featureName="Trend Analysis"
          />
        )}

        {/* Framing fix (Vincent 2026-10-19): this is the AI's reading of the user's own data, not cited
            medical guidance, and must say so before the findings. */}
        <p className="ta-predictionFrame" data-testid="ai-prediction-frame">
          AI analysis of your own data — not cited medical guidance. Discuss any change with your clinician.
        </p>

        {analysis.findings && analysis.findings.length > 0 && (
          <div className="ta-aiBlock">
            <h5 className="ta-aiBlockTitle">Key Findings</h5>
            <ul className="ta-aiList">
              {analysis.findings.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </div>
        )}

        {analysis.correlations && analysis.correlations.length > 0 && (
          <div className="ta-aiBlock">
            <h5 className="ta-aiBlockTitle">Correlations</h5>
            <ul className="ta-aiList">
              {analysis.correlations.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        )}

        {analysis.strengths && analysis.strengths.length > 0 && (
          <div className="ta-aiBlock">
            <h5 className="ta-aiBlockTitle">Areas of Strength</h5>
            <ul className="ta-aiList ta-aiList--strengths">
              {analysis.strengths.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
        )}

        {analysis.improvements && analysis.improvements.length > 0 && (
          <div className="ta-aiBlock">
            <h5 className="ta-aiBlockTitle">Areas for Improvement</h5>
            <ul className="ta-aiList ta-aiList--improvements">
              {analysis.improvements.map((im, i) => <li key={i}>{im}</li>)}
            </ul>
          </div>
        )}

        {analysis.predictions && analysis.predictions.length > 0 && (
          <div className="ta-aiBlock">
            <h5 className="ta-aiBlockTitle">Predictions</h5>
            <ul className="ta-aiList">
              {analysis.predictions.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </div>
        )}

        <MedicalDisclaimer variant="ai" />
      </div>
    );
  };

  // ── Reports Tab ──
  const loadReports = async () => {
    setReportsLoading(true);
    try {
      const [provRes, trainRes, histRes] = await Promise.all([
        authFetch(`${API_BASE}/api/reports/user-reports`),
        authFetch(`${API_BASE}/api/trainer-reports/list`),
        authFetch(`${API_BASE}/api/reports/access-history`),  // P-004 claim 18
      ]);
      if (provRes.ok) {
        const data = await provRes.json();
        setReports(Array.isArray(data) ? data : data.reports || []);
      }
      if (trainRes.ok) {
        const data = await trainRes.json();
        setTrainerReports(Array.isArray(data) ? data : data.reports || []);
      }
      if (histRes.ok) {
        const data = await histRes.json();
        setAccessHistory(data.events || []);
      }
    } catch (err) {
      console.error('Error loading reports:', err);
    } finally {
      setReportsLoading(false);
    }
  };

  // ── #89 objectives (measurement surface) ──────────────────────────────────────────────────
  const [objectives, setObjectives] = useState([]);
  const [objectivesLoading, setObjectivesLoading] = useState(false);
  // Per-row busy id, not a global flag: a page-wide spinner would disable every other row's
  // controls while one archive is in flight.
  const [objectiveBusy, setObjectiveBusy] = useState(null);
  const [objectiveError, setObjectiveError] = useState(null);
  const [objectiveFilter, setObjectiveFilter] = useState('all');
  const [objectiveCounts, setObjectiveCounts] = useState({});

  const loadObjectives = async () => {
    setObjectivesLoading(true);
    try {
      // authFetch, not the raw fetchWithAuth prop — the local wrapper adds the auth headers and
      // falls back to plain fetch when the prop is absent, which every other loader here uses.
      // include_archived so the Archived filter has something to show without a second round-trip.
      const res = await authFetch(`${API_BASE}/api/analytics/objectives?include_archived=true`);
      if (res.ok) {
        const data = await res.json();
        setObjectives(data.objectives || []);
        setObjectiveCounts(data.counts || {});
      }
    } catch (err) {
      console.error('Failed to load objectives:', err);
    } finally {
      setObjectivesLoading(false);
    }
  };

  // #89 Phase 3 — one call site for every lifecycle action (policy / archive / reactivate).
  // Reloads on success so the row moves to its new filter bucket immediately; a stale row
  // still offering "Mark done" after it was archived reads as the action having failed.
  // Failure is SURFACED, never swallowed — a control that silently does nothing is the
  // shape this whole batch exists to remove.
  const objectiveAction = async (objectiveId, action) => {
    setObjectiveBusy(objectiveId);
    setObjectiveError(null);
    try {
      const res = await authFetch(
        `${API_BASE}/api/health/objectives/${objectiveId}/${action}`, { method: 'POST' });
      if (!res.ok) {
        let detail = '';
        try { detail = (await res.json()).detail || ''; } catch (_) {}
        throw new Error(detail || `Request failed (${res.status})`);
      }
      await loadObjectives();
    } catch (err) {
      console.error('Objective lifecycle action failed:', err);
      setObjectiveError(err.message || 'That didn’t save. Please try again.');
    } finally {
      setObjectiveBusy(null);
    }
  };

  useEffect(() => {
    if (mainTab === 'reports') {
      loadReports();
      // Reset send mode when entering reports tab
      setSendMode(null);
      setSendComplete(false);
    }
    if (mainTab === 'objectives') {
      loadObjectives();
    }
  }, [mainTab]);

  const deleteReport = async (reportId, type) => {
    const endpoint = type === 'trainer'
      ? `${API_BASE}/api/trainer-reports/${reportId}`
      : `${API_BASE}/api/reports/${reportId}`;
    try {
      const res = await authFetch(endpoint, { method: 'DELETE' });
      if (res.ok) loadReports();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const openSendMode = async (mode) => {
    setSendMode(mode);
    setSelectedRecipients([]);
    setSelectedData({});
    setSendProgress(0);
    setSendComplete(false);
    try {
      const url = mode === 'provider' ? `${API_BASE}/api/providers` : `${API_BASE}/api/trainers`;
      const res = await authFetch(url);
      if (res.ok) {
        const data = await res.json();
        if (mode === 'provider') setProviders(data || []);
        else setTrainers(data || []);
      }
    } catch (err) {
      console.error('Failed to load recipients:', err);
    }
  };

  const toggleRecipient = (id) => {
    setSelectedRecipients(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  const toggleDataElement = (key) => {
    setSelectedData(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSendReport = async () => {
    if (selectedRecipients.length === 0) return alert('Select at least one recipient');
    const hasData = Object.values(selectedData).some(v => v);
    if (!hasData) return alert('Select at least one data element');

    setSendingReport(true);
    setSendProgress(10);

    const url = sendMode === 'provider'
      ? `${API_BASE}/api/reports/generate`
      : `${API_BASE}/api/trainer-reports/generate`;
    const body = sendMode === 'provider'
      ? { data_elements: selectedData, provider_ids: selectedRecipients }
      : { data_elements: selectedData, trainer_ids: selectedRecipients };

    try {
      const res = await authFetch(url, { method: 'POST', body: JSON.stringify(body) });
      if (!res.ok) { alert('Failed to generate report'); setSendingReport(false); return; }
      const result = await res.json();
      const reportId = result.report_id;
      setSendProgress(30);

      // Poll for progress
      const statusUrl = sendMode === 'provider'
        ? `${API_BASE}/api/reports/${reportId}/status`
        : `${API_BASE}/api/trainer-reports/status/${reportId}`;

      const poll = setInterval(async () => {
        try {
          const statusRes = await authFetch(statusUrl);
          if (statusRes.ok) {
            const status = await statusRes.json();
            setSendProgress(status.progress_percentage || 50);
            if (status.generation_status === 'completed' || status.generation_status === 'sent') {
              clearInterval(poll);
              setSendComplete(true);
              setSendingReport(false);
              loadReports();
            } else if (status.generation_status === 'failed') {
              clearInterval(poll);
              alert('Report generation failed');
              setSendingReport(false);
            }
          }
        } catch { clearInterval(poll); setSendingReport(false); }
      }, 2000);
    } catch (err) {
      console.error('Send report error:', err);
      setSendingReport(false);
    }
  };

  // P-004 claims 3/4/17: hierarchical scope taxonomy — 5 canonical categories.
  const SCOPE_CATEGORIES = ['Clinical', 'Biometric', 'Nutrition', 'Fitness', 'Documents'];
  const DATA_ELEMENTS = [
    { key: 'prescriptions', label: 'Prescriptions', cat: 'Clinical' },
    { key: 'bodyCharacteristics', label: 'Body Characteristics', cat: 'Biometric' },
    { key: 'deviceData', label: 'Device Data', cat: 'Biometric' },
    { key: 'healthObjectives', label: 'Health Objectives', cat: 'Biometric' },
    { key: 'foodPreferences', label: 'Food Preferences', cat: 'Nutrition' },
    { key: 'dietPlans', label: 'Diet Plans', cat: 'Nutrition' },
    { key: 'mealPlans', label: 'Meal Plans', cat: 'Nutrition' },
    { key: 'exercisePlans', label: 'Exercise Plans', cat: 'Fitness' },
    { key: 'weightTrainingPlans', label: 'Weight Training Plans', cat: 'Fitness' },
    { key: 'medicalRecords', label: 'Medical Records', cat: 'Documents' },
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      });
    } catch { return dateStr; }
  };

  // ── #89 OBJECTIVES TAB — progress MEASURED from health data, never self-reported ──────────
  const renderObjectivesTab = () => {
    const FILTERS = [
      ['all', 'All'], ['active', 'Active'], ['maintaining', 'Maintaining'],
      ['achieved', 'Achieved'], ['archived', 'Archived'],
    ];
    const visible = objectiveFilter === 'all'
      ? objectives.filter(o => o.state !== 'archived')
      : objectives.filter(o => o.state === objectiveFilter);

    const originChip = (origin) => {
      const map = { manual: ['obj-chipManual', 'MANUAL'],
                    onboarding: ['obj-chipOnboard', 'FROM ONBOARDING'],
                    discovered: ['obj-chipDisc', 'DISCOVERED'] };
      const [cls, label] = map[origin] || map.manual;
      return <span className={`obj-chip ${cls}`}>{label}</span>;
    };

    const stateChip = (state) => {
      if (state === 'maintaining') return <span className="obj-chip obj-chipMaint">MAINTAINING</span>;
      if (state === 'achieved') return <span className="obj-chip obj-chipAch">ACHIEVED</span>;
      if (state === 'archived') return <span className="obj-chip obj-chipArch">ARCHIVED</span>;
      return null;
    };

    // Gold arc, matching the Longevity gauge rather than inventing a second progress visual.
    const ring = (pct, state) => {
      const CIRC = 232;
      const p = pct == null ? 0 : Math.max(0, Math.min(1, pct));
      const done = state === 'achieved' || state === 'archived';
      return (
        <div className="obj-ring">
          <svg width="88" height="88" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="44" cy="44" r="37" fill="none" stroke="#E5E7EB" strokeWidth="9" />
            <circle cx="44" cy="44" r="37" fill="none"
                    stroke={done ? '#1E3A5F' : '#C9A962'} strokeWidth="9" strokeLinecap="round"
                    strokeDasharray={CIRC} strokeDashoffset={CIRC * (1 - p)} />
          </svg>
          <div className="obj-ringVal">
            {done ? '✓' : (pct == null ? '—' : `${Math.round(p * 100)}%`)}
            <small>{state === 'maintaining' ? 'maintaining' : done ? 'achieved' : 'to target'}</small>
          </div>
        </div>
      );
    };

    const sparkline = (series) => {
      if (!series || series.length < 2) return null;
      const vals = series.map(s => s.value).filter(v => v != null);
      if (vals.length < 2) return null;
      const min = Math.min(...vals), max = Math.max(...vals);
      const span = (max - min) || 1;
      const pts = vals.map((v, i) =>
        `${(i / (vals.length - 1)) * 280},${34 - ((v - min) / span) * 30}`).join(' ');
      return (
        <svg className="obj-spark" viewBox="0 0 280 34" preserveAspectRatio="none">
          <polyline points={pts} fill="none" stroke="#1E3A5F" strokeWidth="2" />
        </svg>
      );
    };

    // WHERE THE NUMBER CAME FROM. A bare percentage is a claim; this is the evidence.
    const provenance = (o) => {
      if (!o.is_tracked) {
        return (
          <div className="obj-prov">
            Not being measured yet — this goal isn’t linked to a health metric.
            Add a target in <b>Health → Health Objectives</b> to start tracking it.
          </div>
        );
      }
      if (!o.measurement) {
        return (
          <div className="obj-prov">
            Waiting for data. This goal is measured from <b>{o.metric?.label || o.metric?.key}</b>,
            and tracking begins as soon as your readings arrive.
          </div>
        );
      }
      const m = o.measurement;
      const n = m.basis?.n;
      const w = m.basis?.window_days;
      const tierLabel = { 1: 'clinical records', 2: 'uploaded documents',
                          3: 'medical-grade device', 4: 'provider', 5: 'wearable',
                          6: 'entered by hand' }[m.dop_tier] || 'unknown source';
      return (
        <div className="obj-prov">
          Measured from <b>{m.data_source}</b> · {tierLabel}
          {m.confidence != null && ` · confidence ${m.confidence.toFixed(2)}`}
          {n && w ? ` · ${n} readings over ${w} days` : n ? ` · ${n} reading${n > 1 ? 's' : ''}` : ''}
          {m.self_reported && ' · entered by you'}
        </div>
      );
    };

    // ── #89 Phase 3 — lifecycle controls ────────────────────────────────────────────────
    // Reaching a goal poses a question only the user can answer (design §2.6). The push
    // notification asks it at the moment of achievement; this is the same choice for anyone
    // who missed or dismissed that — otherwise the decision is reachable only from a
    // notification that has already gone.
    //
    // Nothing is shown for a plain in-progress objective: an Archive button on a goal
    // someone is still working toward invites an accidental tap that quietly removes it
    // from the UHC.
    const lifecycleControls = (o) => {
      if (o.state === 'archived') {
        return (
          <div className="obj-actions">
            <span className="obj-muted">
              Archived{o.archived_at ? ` ${new Date(o.archived_at).toLocaleDateString()}` : ''} ·
              history kept
            </span>
            <button className="btn btn-secondary btn-sm"
                    data-testid="obj-reactivate"
                    disabled={objectiveBusy === o.id}
                    onClick={() => objectiveAction(o.id, 'reactivate')}>
              Reactivate
            </button>
          </div>
        );
      }

      // Achieved but unanswered — the A/B, surfaced in the row per §2.6's "cap at 3 prompts
      // then surface the choice in the Analytics row".
      if ((o.state === 'achieved' || o.state === 'maintaining') && !o.achievement_policy) {
        return (
          <div className="obj-actions" data-testid="obj-policy-choice">
            <span className="obj-muted">Reached it — keep tracking, or mark it done?</span>
            <button className="btn btn-primary btn-sm"
                    data-testid="obj-policy-monitor"
                    disabled={objectiveBusy === o.id}
                    onClick={() => objectiveAction(o.id, 'policy?policy=monitor')}>
              Keep tracking
            </button>
            <button className="btn btn-secondary btn-sm"
                    data-testid="obj-policy-archive"
                    disabled={objectiveBusy === o.id}
                    onClick={() => objectiveAction(o.id, 'policy?policy=archive')}>
              Mark done
            </button>
          </div>
        );
      }

      if (o.state === 'maintaining') {
        return (
          <div className="obj-actions">
            <span className="obj-muted">
              Reached — still measured, so we can tell you if it slips.
            </span>
            <button className="btn btn-secondary btn-sm"
                    data-testid="obj-archive"
                    disabled={objectiveBusy === o.id}
                    onClick={() => objectiveAction(o.id, 'archive')}>
              Mark done
            </button>
          </div>
        );
      }
      return null;
    };

    return (
      <div className="obj-tab">
        {objectiveError && (
          <div className="obj-actionError" data-testid="obj-action-error">{objectiveError}</div>
        )}
        <div className="obj-filters">
          {FILTERS.map(([key, label]) => {
            const n = key === 'all'
              ? objectives.filter(o => o.state !== 'archived').length
              : (objectiveCounts[key] || 0);
            return (
              <button
                key={key}
                className={`btn btn-sm ${objectiveFilter === key ? 'btn-secondary' : 'btn-ghost'}`}
                onClick={() => setObjectiveFilter(key)}
                data-testid={`obj-filter-${key}`}
              >
                {label} ({n})
              </button>
            );
          })}
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Progress against your objectives</h3>
          </div>
          <div className="card-body" data-testid="obj-list">
            {objectivesLoading ? (
              <p className="obj-muted">Loading your objectives…</p>
            ) : visible.length === 0 ? (
              <div className="obj-empty">
                <p className="obj-muted">
                  {objectives.length === 0
                    ? 'No objectives yet — set one in Health → Health Objectives and Healist will track it from your health data.'
                    : 'No objectives in this state.'}
                </p>
                {objectives.length === 0 && (
                  <HHSInstallNudge
                    reason="Most goals are tracked from your watch and health data."
                    onOpenTutorial={() => setActiveTab && setActiveTab('tutorials')}
                  />
                )}
              </div>
            ) : (
              <>
                {visible.map(o => (
                  <div className="obj-row" key={o.id} data-testid="obj-row">
                    {ring(o.progress_pct, o.state)}
                    <div className="obj-main">
                      {originChip(o.origin)} {stateChip(o.state)}
                      <div className="obj-title">{o.title}</div>
                      <div className="obj-vals">
                        <div className="obj-muted">Baseline
                          <span>{o.baseline_value ?? '—'}{o.unit ? ` ${o.unit}` : ''}</span></div>
                        <div className="obj-muted">Current
                          <span>{o.current_value ?? '—'}{o.unit ? ` ${o.unit}` : ''}</span></div>
                        <div className="obj-muted">Target
                          <span>{o.target_value ?? '—'}{o.unit ? ` ${o.unit}` : ''}</span></div>
                      </div>
                      {sparkline(o.series)}
                      {provenance(o)}
                      {lifecycleControls(o)}
                    </div>
                  </div>
                ))}
                {/* The promise, restated where it is easiest to doubt. */}
                <p className="obj-footnote">
                  Progress is computed from your health data — it is never self-reported.
                </p>
              </>
            )}
          </div>
        </div>

        {/* Anything untracked gets ONE nudge at the bottom rather than a repeat per row. */}
        {!objectivesLoading && objectives.length > 0 && objectives.some(o => !o.is_tracked) && (
          <HHSInstallNudge
            reason="Some of your goals aren’t being measured yet."
            compact
            onOpenTutorial={() => setActiveTab && setActiveTab('tutorials')}
          />
        )}
      </div>
    );
  };

  const renderReportsTab = () => {
    // Combine and sort all reports
    const allReports = [
      ...reports.map(r => ({ ...r, _type: 'provider' })),
      ...trainerReports.map(r => ({ ...r, _type: 'trainer' })),
    ].sort((a, b) => new Date(b.created_at || b.sent_at || 0) - new Date(a.created_at || a.sent_at || 0));

    const recipientList = sendMode === 'provider' ? providers : trainers;
    const recipientLabel = sendMode === 'provider' ? 'Provider' : 'Trainer';

    return (
      <div className="ta-reportsSection">
        {/* Send New Report */}
        <h4 className="ta-reportsSectionTitle">Send New Report</h4>

        {!sendMode && (
          <div className="ta-sendReportGrid">
            <div className="ta-sendReportCard">
              <div className="ta-sendReportIcon">&#x1F3E5;</div>
              <div className="ta-sendReportTitle">Send to Physicians / Providers</div>
              <p className="ta-sendReportDesc">
                Share health data with your healthcare providers via encrypted OTP-protected report
              </p>
              <button className="btn btn-primary ta-sendReportBtn" onClick={() => openSendMode('provider')}>
                Send Provider Report
              </button>
            </div>
            <div className="ta-sendReportCard">
              <div className="ta-sendReportIcon">&#x1F3CB;&#xFE0F;</div>
              <div className="ta-sendReportTitle">Send to Trainers / Nutritionists</div>
              <p className="ta-sendReportDesc">
                Share fitness and nutrition data with your trainers via encrypted OTP-protected report
              </p>
              <button className="btn btn-primary ta-sendReportBtn" onClick={() => openSendMode('trainer')}>
                Send Trainer Report
              </button>
            </div>
          </div>
        )}

        {sendMode && !sendComplete && (
          <div className="ta-sendForm">
            <div className="ta-sendFormHeader">
              <h4>Send {recipientLabel} Report</h4>
              <button className="btn btn-ghost btn-sm" onClick={() => setSendMode(null)}>Cancel</button>
            </div>

            {/* Recipients */}
            <div className="ta-sendFormSection">
              <label className="ta-sendFormLabel">Select {recipientLabel}s</label>
              {recipientList.length === 0 ? (
                <p className="ta-sendFormEmpty">No {recipientLabel.toLowerCase()}s found. Add them first in {sendMode === 'provider' ? 'Health' : 'Fitness'} Management.</p>
              ) : (
                <div className="ta-sendCheckboxGrid">
                  {recipientList.map(r => (
                    <label key={r.id} className="ta-sendCheckbox">
                      <input
                        type="checkbox"
                        checked={selectedRecipients.includes(r.id)}
                        onChange={() => toggleRecipient(r.id)}
                      />
                      <span>{r.provider_name || r.trainer_name || r.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Data Elements */}
            <div className="ta-sendFormSection">
              <label className="ta-sendFormLabel">Data to Include</label>
              <div className="ta-sendCheckboxGrid">
                {SCOPE_CATEGORIES.map(cat => {
                  const items = DATA_ELEMENTS.filter(d => d.cat === cat && (sendMode === 'trainer' ? d.key !== 'medicalRecords' : true));
                  if (!items.length) return null;
                  return (
                    <div key={cat} className="ta-scopeCategory">
                      <h5 className="ta-scopeCategoryHeader scope-category-header">{cat}</h5>
                      {items.map(d => (
                        <label key={d.key} className="ta-sendCheckbox">
                          <input
                            type="checkbox"
                            checked={!!selectedData[d.key]}
                            onChange={() => toggleDataElement(d.key)}
                          />
                          <span>{d.label}</span>
                        </label>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Progress */}
            {sendingReport && (
              <div className="ta-sendProgress">
                <div className="ta-sendProgressBar">
                  <div className="ta-sendProgressFill" style={{ width: `${sendProgress}%` }} />
                </div>
                <span className="ta-sendProgressText">Generating report... {sendProgress}%</span>
              </div>
            )}

            <div className="ta-sendFormActions">
              <button
                className="btn btn-primary"
                onClick={handleSendReport}
                disabled={sendingReport || selectedRecipients.length === 0}
              >
                {sendingReport ? 'Sending...' : `Send ${recipientLabel} Report`}
              </button>
            </div>
          </div>
        )}

        {sendComplete && (
          <div className="ta-sendComplete">
            <div className="ta-sendCompleteIcon">&#x2705;</div>
            <h4>Report Sent Successfully!</h4>
            <p>Your {recipientLabel.toLowerCase()} report has been generated and sent.</p>
            <button className="btn btn-primary" onClick={() => { setSendMode(null); setSendComplete(false); }}>
              Done
            </button>
          </div>
        )}

        {/* Report History */}
        {accessHistory.length > 0 && (
          <div className="ta-accessHistorySection">
            <h4 className="ta-reportsSectionTitle">Provider Access History</h4>
            <div className="ta-accessHistoryList">
              {accessHistory.map((ev, i) => (
                <div key={i} className="ta-accessHistoryRow">
                  <span>{ev.event === 'VALIDATE' ? 'Accessed' : ev.event === 'REVOKE' ? 'Revoked' : ev.event}{ev.report_title ? ` — ${ev.report_title}` : ''}</span>
                  <span className="ta-accessHistoryDate">{ev.at ? new Date(ev.at).toLocaleString() : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <h4 className="ta-reportsSectionTitle">Report History</h4>
        {reportsLoading ? (
          <div className="ta-loadingContainer">
            <div className="ta-spinner"></div>
            <p>Loading reports...</p>
          </div>
        ) : allReports.length === 0 ? (
          <div className="ta-reportsEmpty">
            <p>No reports sent yet. Use the cards above to send your first report.</p>
          </div>
        ) : (
          <div className="ta-reportsList">
            <div className="ta-reportsListHeader">
              <span className="ta-reportsCol ta-reportsCol--recipient">Recipient</span>
              <span className="ta-reportsCol ta-reportsCol--type">Type</span>
              <span className="ta-reportsCol ta-reportsCol--date">Date Sent</span>
              <span className="ta-reportsCol ta-reportsCol--status">Status</span>
              <span className="ta-reportsCol ta-reportsCol--actions">Actions</span>
            </div>
            {allReports.map(report => {
              const recipientNames = report.recipients
                ? report.recipients.map(r => r.provider_name || r.trainer_name || r.name).join(', ')
                : (report.provider_names && report.provider_names.length > 0)
                  ? report.provider_names.join(', ')
                  : report.report_title || report.trainer_name || 'Report';
              const status = report.generation_status || report.status || 'sent';
              const statusClass = status === 'sent' ? 'ta-reportStatus--sent'
                : status === 'responded' ? 'ta-reportStatus--responded'
                : 'ta-reportStatus--pending';
              return (
                <div className="ta-reportsListRow" key={`${report._type}-${report.id}`}>
                  <span className="ta-reportsCol ta-reportsCol--recipient">{recipientNames}</span>
                  <span className="ta-reportsCol ta-reportsCol--type">
                    <span className={`ta-reportTypeBadge ta-reportTypeBadge--${report._type}`}>
                      {report._type === 'provider' ? 'Provider' : 'Trainer'}
                    </span>
                  </span>
                  <span className="ta-reportsCol ta-reportsCol--date">
                    {formatDate(report.created_at || report.sent_at)}
                  </span>
                  <span className="ta-reportsCol ta-reportsCol--status">
                    <span className={`ta-reportStatus ${statusClass}`}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                  </span>
                  <span className="ta-reportsCol ta-reportsCol--actions">
                    {(status === 'sent' || status === 'completed') && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={async () => {
                          const endpoint = report._type === 'provider'
                            ? `${API_BASE}/api/reports/${report.id}/download`
                            : `${API_BASE}/api/trainer-reports/${report.id}/download`;
                          try {
                            setViewingReportTitle(`${report._type === 'provider' ? 'Provider' : 'Trainer'} Report`);
                            setReportPdfLoading(true);
                            setViewingReportUrl('loading');
                            const res = await authFetch(endpoint);
                            if (!res.ok) { alert('PDF not available'); setViewingReportUrl(null); setReportPdfLoading(false); return; }
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            setViewingReportUrl(url);
                          } catch (err) {
                            alert('Failed to load report');
                            setViewingReportUrl(null);
                            setReportPdfLoading(false);
                          }
                        }}
                        title="View report PDF"
                      >
                        View
                      </button>
                    )}
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => deleteReport(report.id, report._type)}
                      title="Delete report"
                    >
                      Delete
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── Main Render ──
  const isHubMode = !!onBack;

  return (
    <div className={`page-layout ta-pageWrapper ${isHubMode ? 'ta-pageWrapper--hub' : ''}`}>
      <div className="page-scroll-content">
      <div className="ta-cardWrapper">
        {/* Header */}
        <div className="ta-modalHeader">
          {isHubMode && onBack && (
            <button onClick={onBack} className="ta-backButton">&larr;</button>
          )}
          <h3 className="ta-pageTitle">Analytics & Reporting</h3>
          {isHubMode && (
            <button onClick={onBack} className="ta-closeButton">&times;</button>
          )}
          {!isHubMode && setActiveTab && (
            <button onClick={() => setActiveTab('dashboard')} className="ta-homeButton">
              &larr; Dashboard
            </button>
          )}
        </div>

        <div className="ta-modalBody">
          {/* Date Range Selector */}
          <div className="ta-dateRange">
            {DATE_RANGES.map(r => (
              <button
                key={r.value}
                className={`ta-dateRangeBtn ${days === r.value ? 'ta-dateRangeBtn--active' : ''}`}
                onClick={() => setDays(r.value)}
              >
                {r.label}
              </button>
            ))}
          </div>

          {/* KPI Cards */}
          {loading ? (
            <div className="ta-loadingContainer">
              <div className="ta-spinner"></div>
              <p>Loading analytics...</p>
            </div>
          ) : error ? (
            <div className="ta-errorContainer">
              <p>{error}</p>
              <button className="btn btn-primary" onClick={loadTrends}>Retry</button>
            </div>
          ) : (
            <>
              {/* KPI Grid */}
              {trendData?.kpis && trendData.kpis.length > 0 && (
                <div className="ta-kpiGrid">
                  {trendData.kpis.map(kpi => renderKpiCard(kpi))}
                </div>
              )}

              {/* Main Tabs: Trends | Reports | Objectives */}
              <div className="ta-mainTabs">
                <button
                  className={`ta-mainTab ${mainTab === 'trends' ? 'ta-mainTab--active' : ''}`}
                  onClick={() => setMainTab('trends')}
                >
                  Trends
                </button>
                <button
                  className={`ta-mainTab ${mainTab === 'reports' ? 'ta-mainTab--active' : ''}`}
                  onClick={() => setMainTab('reports')}
                >
                  Reports
                </button>
                {/* #89 — the MEASUREMENT surface. Declaration lives in Health → Health Objectives;
                    progress lives here, because putting it under Health would imply the user can
                    edit it, which is exactly what must not be true. */}
                <button
                  className={`ta-mainTab ${mainTab === 'objectives' ? 'ta-mainTab--active' : ''}`}
                  onClick={() => setMainTab('objectives')}
                >
                  Objectives
                </button>
              </div>

              {mainTab === 'objectives' ? (
                renderObjectivesTab()
              ) : mainTab === 'trends' ? (
                <>
                  {/* Category Sub-Tabs */}
                  <div className="ta-categoryTabs">
                    {CATEGORY_TABS.map(tab => (
                      <button
                        key={tab.id}
                        className={`ta-categoryTab ${category === tab.id ? 'ta-categoryTab--active' : ''}`}
                        onClick={() => setCategory(tab.id)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Chart Area */}
                  {trendData?.charts && trendData.charts.map((chart, i) => (
                    <div key={i}>
                      {renderBarChart(chart)}
                    </div>
                  ))}

                  {/* Trend Summaries */}
                  {trendData?.summaries && trendData.summaries.length > 0 && (
                    <div className="ta-summariesSection">
                      <h4 className="ta-summariesTitle">Trend Summaries</h4>
                      <div className="ta-summariesGrid">
                        {trendData.summaries.map(s => renderTrendSummary(s))}
                      </div>
                    </div>
                  )}

                  {/* AI Analysis */}
                  {renderAiAnalysis()}
                </>
              ) : (
                renderReportsTab()
              )}
            </>
          )}
        </div>
      </div>

      {/* Report PDF Viewer Modal */}
      {viewingReportUrl && (
        <div className="modal-overlay" onClick={() => { if (viewingReportUrl !== 'loading') URL.revokeObjectURL(viewingReportUrl); setViewingReportUrl(null); setReportPdfLoading(false); }}>
          <div className="rv-modal" onClick={e => e.stopPropagation()}>
            <div className="rv-header">
              <h3 className="rv-title">{viewingReportTitle}</h3>
              <button onClick={() => { if (viewingReportUrl !== 'loading') URL.revokeObjectURL(viewingReportUrl); setViewingReportUrl(null); setReportPdfLoading(false); }} className="rv-closeBtn">&times;</button>
            </div>
            <div className="rv-content" style={{ position: 'relative', minHeight: '70vh' }}>
              {reportPdfLoading && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '16px', background: 'white', zIndex: 2 }}>
                  <div className="spinner" style={{ width: '36px', height: '36px', borderWidth: '3px', borderColor: 'rgba(30,58,95,0.2)', borderTopColor: '#1E3A5F' }}></div>
                  <p style={{ color: '#1E3A5F', fontWeight: 500, margin: 0 }}>Decrypting Report for Viewing...</p>
                </div>
              )}
              {viewingReportUrl !== 'loading' && (
                <iframe
                  src={viewingReportUrl}
                  className="rv-iframe"
                  title={viewingReportTitle}
                  onLoad={() => setReportPdfLoading(false)}
                />
              )}
            </div>
            <div className="rv-footer">
              <em className="ta-printHint">Use the PDF toolbar to Print</em>
              <button onClick={() => { if (viewingReportUrl !== 'loading') URL.revokeObjectURL(viewingReportUrl); setViewingReportUrl(null); setReportPdfLoading(false); }} className="rv-closeFooterBtn">Dismiss</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default TrendsAnalysis;
